import React, { useEffect, useState } from "react";
import {
  DataTable,
  DataTableColumn,
  DateRange,
  DateRangeInput, Icon,
  Input,
  Loader,
  PaginationPageSizes,
  Select,
  useDebounce
} from "best-common-react";
import styled from "styled-components";
import { searchMarketingCampaigns } from "../api/AuthenticatedClient.ts";
import { MarketingCampaignFilters, MarketingCampaignRow } from "../types/marketing-campaign.ts";
import { CreatedAtFormatter, TeamFormatter } from "../components/TableFormatters.tsx";
import { UploadedByOptions } from "../constants/common.ts";
import { useDropdowns } from "../contexts/DropdownsContext.tsx";

const Container = styled.div`
  height: 100%;
  width: 100%;
  padding: 1rem;
`;

const HeaderContainer = styled.div`
  padding-bottom: 1rem;
  border-bottom: 1px solid #000;
`;

const TitleContainer = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FilterInput = styled.div`
  width: 250px;
`;

const TableContainer = styled.div`
  margin-top: 1rem;
`;

const BrowseMarketingCampaigns: React.FC = () => {
  const { teamOptions } = useDropdowns();
  const [searchTermDisplay, setSearchTermDisplay] = React.useState<string>("");
  const [filters, setFilters] = useState<MarketingCampaignFilters>({
    search: "",
    createdBy: "Me",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [marketingCampaigns, setMarketingCampaigns] = React.useState<MarketingCampaignRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState<PaginationPageSizes>(25);
  const [loading, setLoading] = useState(false);

  const handleFilterChange = (key: keyof MarketingCampaignFilters, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPageNumber(0);
  };

  const onSearchMarketingCampaigns = () => {
    setLoading(true);
    searchMarketingCampaigns(filters, pageNumber, pageSize).then(res => {
      setMarketingCampaigns(res.content);
      setTotalItems(res.page.totalElements);
      setLoading(false);
    });
  }

  useEffect(() => {
    onSearchMarketingCampaigns();
  }, [filters, pageNumber, pageSize]);

  const Columns: DataTableColumn<MarketingCampaignRow>[] = [
    {
      name: "Team",
      key: "",
      width: 50,
      readonlyFormatter: TeamFormatter
    },
    {
      name: "Campaign Type",
      key: "campaignType",
      width: 200
    },
    {
      name: "Audience ID",
      key: "audienceId",
      width: 125
    },
    {
      name: "Name",
      key: "name",
      width: 250
    },
    {
      name: "Query Template",
      key: "queryTemplate",
      width: 125
    },
    {
      name: "Recurrence",
      key: "recurrence",
      width: 100
    },
    {
      name: "Run Time",
      key: "runTime",
      width: 75
    },
    {
      name: "DOW",
      key: "dayOfWeek",
      width: 75
    },
    {
      name: "DOM",
      key: "dayOfMonth",
      width: 75
    },
    {
      name: "Created By",
      key: "createdBy",
      width: 150
    },
    {
      name: "Created At",
      key: "createdAt",
      width: 250,
      readonlyFormatter: CreatedAtFormatter
    }
  ];

  const onSearchTermChange = useDebounce(async (value: string) => {
    setFilters({ ...filters, search: value });
  }, 400);

  return (
    <Container>
      <HeaderContainer>
        <TitleContainer>
          <span>Browse Marketing Campaigns</span>
          {loading ? <Loader type="spinner" size="20" /> : <Icon iconName="fa-refresh" onClick={onSearchMarketingCampaigns} />}
        </TitleContainer>

        <FiltersContainer>
          <FilterInput>
            <Input
              id="search"
              label="Search"
              value={searchTermDisplay}
              onChange={value => {
                setSearchTermDisplay(value);
                onSearchTermChange(value);
              }}
            />
          </FilterInput>

          <FilterInput>
            <Select
              id="club-select"
              label="Club"
              value={teamOptions.find(option => option.value.id === filters.teamId)}
              options={teamOptions}
              clearable
              onChange={option => {
                handleFilterChange("teamId", option?.value.id);
              }}
            />
          </FilterInput>

          <FilterInput>
            <Select
              id="created-by-select"
              label="Created By"
              value={UploadedByOptions.find(option => option.value === filters.createdBy)}
              options={UploadedByOptions}
              onChange={option => {
                handleFilterChange("createdBy", option?.value);
              }}
            />
          </FilterInput>

          <div>
            <DateRangeInput
              id="birth-date-select"
              label="Created"
              value={{
                start: filters.createdFrom,
                end: filters.createdTo
              }}
              onChange={(value: DateRange<Date> | "N/A") => {
                const { start, end } = value as DateRange<Date>;
                setFilters({ ...filters, createdFrom: start, createdTo: end });
              }}
              clearable
              invalidDateMessage=""
            />
          </div>
        </FiltersContainer>
      </HeaderContainer>
      <TableContainer>
        <DataTable
          data={marketingCampaigns}
          columns={Columns}
          tableHeights={{
            autogrow: true,
            maxHeightOffset: 400
          }}
          pagination={{
            totalItems,
            pageNumber,
            setPageNumber,
            pageSize,
            setPageSize
          }}
        />
      </TableContainer>
    </Container>
  );
};

export default BrowseMarketingCampaigns;
